import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { useQueryClient } from 'react-query';
// Deep imports rather than the `src/components` barrel — see CompareSlider.
import Button from 'src/components/Button';
import Input from 'src/components/Input';
import Textarea from 'src/components/Textarea';
import { useAccessToken } from 'src/hooks/useAccessToken';
import { useImageEditAvailable } from 'src/hooks/useImageEditAvailable';
import { useRemoveBackgroundAvailable } from 'src/hooks/useRemoveBackgroundAvailable';
import { useAlerts } from 'src/common/AlertManager';
import { CaretDown, CaretLeft, CaretRight, Checks } from 'src/common/icons';
import { getCutout } from 'src/api/functions/addonsApi';
import {
  createNewAsset,
  photoEditConfirm,
  photoEditDetect,
  photoEditDiscard,
  updateAssetConversionMedia,
  PhotoEditPreviewData,
} from 'src/api/functions/assetsApi';
import { queryNames } from 'src/api/utils/queryNames';
import { useAiRatesQuery } from 'src/api/queries/subscriptionQueries';
import { getThumbnailUrl, addCacheBuster } from 'src/utils/previews';

import {
  CUTOUT_OUTCOMES,
  CutoutOutcome,
  CutoutOutcomeId,
  applyCreditsFor,
  buildGenerativeRequest,
  buildMatteRequest,
  detectCreditsFor,
  getCutoutOutcome,
  previewCreditsFor,
} from './cutoutOutcomes';
import { serverPresetFor } from './removeObjectPresets';
import { CompareSlider } from './CompareSlider';
import { SaveResultForm, NameProblem, SaveDisposition, validateResultName } from './SaveResultForm';
import { usePhotoEditPreview } from './usePhotoEditPreview';
import messages, { saveErrorMessage } from '../messages';
import { isErrorResponseResult } from 'src/api/utils/errorHandlers';
import styles from './CutoutDialog.module.scss';

const REFINE_MAX = 200;
/** The outcome to escalate to when the user wants what a matte cannot do. */
const ESCALATE_TO: CutoutOutcomeId = 'ghost_mannequin';

export type CutoutDialogProps = {
  assetId: string;
  assetName: string;
  /** Custom data of the source asset, copied when saving a new asset. */
  assetData?: any;
  /** Fired after a result is persisted, so the host can refresh + close. */
  onConfirmed: (disposition: SaveDisposition) => void;
};

/**
 * "Cut out / change background".
 *
 * Renders inline in the asset modal, same slot as the AI edit panel; the host
 * supplies the header and Back button.
 *
 * The user picks an outcome and the registry picks the engine — a 2-credit
 * BiRefNet matte for the two plain cut-outs, the 8-credit generative op only
 * where pixels have to be rebuilt. See `cutoutOutcomes.ts`.
 *
 * Choosing an outcome collapses the tile list to a one-line summary. That is
 * not decoration: four tiles plus an expanded Adjust needs ~1115px in a 540px
 * column, so without the collapse the action bar is pushed out of sight.
 */
export const CutoutDialog: React.FC<CutoutDialogProps> = ({
  assetId,
  assetName,
  assetData,
  onConfirmed,
}) => {
  const { formatMessage } = useIntl();
  const accessToken = useAccessToken();
  const queryClient = useQueryClient();
  const { showSuccess, showAlert, showInfo } = useAlerts();

  const [originalUrl, setOriginalUrl] = useState('');
  const [outcomeId, setOutcomeId] = useState<CutoutOutcomeId | null>(null);
  const [adjustOpen, setAdjustOpen] = useState(false);
  const [running, setRunning] = useState(false);

  // A result from either engine, normalised to what the stage and the save step
  // need. `preview` is set for the generative path only — its save goes through
  // the confirm endpoint with a token, whereas a matte's bytes are already here.
  const [resultUrl, setResultUrl] = useState<string | null>(null);
  const [matteBytes, setMatteBytes] = useState<{ base64: string; contentType: string } | null>(null);
  const [preview, setPreview] = useState<PhotoEditPreviewData | null>(null);

  // Public by default: a cut-out is normally produced in order to be
  // published. Applies to the conversion disposition only — a new asset's
  // original is always private (the server forces it).
  const [isPublic, setIsPublic] = useState(true);
  // A new asset normally wants the source's tags and fields.
  const [inheritMetadata, setInheritMetadata] = useState(true);
  const [confirmMode, setConfirmMode] = useState<SaveDisposition | null>(null);
  const [resultName, setResultName] = useState('');
  const [nameProblem, setNameProblem] = useState<NameProblem | null>(null);
  const [confirming, setConfirming] = useState(false);

  // Generative adjustments.
  const [subject, setSubject] = useState('');
  const [editingSubject, setEditingSubject] = useState(false);
  const [removeTokens, setRemoveTokens] = useState<string[]>([]);
  const [detectedRemovables, setDetectedRemovables] = useState<string[]>([]);
  const [refinePrompt, setRefinePrompt] = useState('');
  // User's pick from the outcome's backgroundOptions; '' = the preset default.
  const [background, setBackground] = useState('');
  const [detecting, setDetecting] = useState(false);
  // Detection is shaped per prompt template server-side (?preset=), so it is
  // fetched (and the prefills reset) per shaping key, not once per dialog.
  const detectKeyRef = useRef<string | null>(null);
  const detectCacheRef = useRef<Map<string, { subject: string; removeSuggestions: string[] }>>(
    new Map(),
  );

  const cancelledRef = useRef(false);
  // Mirrors editingSubject for the detect closure: a detect response landing
  // while the user is typing the subject must not clobber their text.
  const editingSubjectRef = useRef(false);
  editingSubjectRef.current = editingSubject;
  const stagedTokenRef = useRef<string | null>(null);
  const confirmedRef = useRef(false);
  const assetIdRef = useRef(assetId);
  const accessTokenRef = useRef(accessToken);
  assetIdRef.current = assetId;
  accessTokenRef.current = accessToken;

  const { submitPreview } = usePhotoEditPreview({ assetId, cancelledRef });

  /*
   * The two engines are gated by two independent per-user addons
   * (REMOVE_BACKGROUND for the matte, AI_IMAGE_EDIT for the generative op), so
   * an account can hold one grant and not the other. Offer only the tiles that
   * would actually run: a 403 on click teaches the user nothing they could have
   * known beforehand. The menu item itself requires at least one of the two.
   */
  const matteAvailable = useRemoveBackgroundAvailable();
  const generativeAvailable = useImageEditAvailable();
  const offered = CUTOUT_OUTCOMES.filter((o) =>
    o.engine === 'matte' ? matteAvailable : generativeAvailable,
  );

  const outcome = getCutoutOutcome(outcomeId || undefined);
  const isGenerative = outcome?.engine === 'generative';

  /*
   * Credit figures come from the live rate card (`ai_credit_rates` via
   * /billing/ai-rates): the rates are sysadmin-editable, so a hardcoded
   * number would quietly start lying after a re-price — and would disagree
   * with the Enhance-with-AI panel, which already reads them live. Until the
   * card resolves these are `undefined` and every price is simply left out.
   */
  const { data: aiRates } = useAiRatesQuery();
  const flatRates = aiRates?.flat;
  const applyCredits = (o: CutoutOutcome) => applyCreditsFor(o, flatRates);
  const previewCredits = (o: CutoutOutcome) => previewCreditsFor(o, flatRates);
  const detectCredits = (o: CutoutOutcome) => detectCreditsFor(o, flatRates);

  useEffect(() => {
    cancelledRef.current = false;
    // A different asset means a different scene: drop the previous asset's
    // detection (and any prefills built from it) rather than serving it from
    // the dialog-lifetime cache.
    detectKeyRef.current = null;
    detectCacheRef.current.clear();
    setSubject('');
    setEditingSubject(false);
    setRemoveTokens([]);
    setDetectedRemovables([]);
    setBackground('');
    // Display-only "before" image: the CDN's optimised display image, same as
    // the AI edit panel. The backend always works from the real original.
    setOriginalUrl(
      addCacheBuster(getThumbnailUrl({ width: 1600, height: 1200, id: assetId, jwt: accessToken })),
    );
    return () => {
      cancelledRef.current = true;
      // A staged generative preview costs credits and holds a server-side
      // object; discard it if the user walks away without saving.
      if (stagedTokenRef.current && !confirmedRef.current) {
        photoEditDiscard(assetIdRef.current, stagedTokenRef.current, accessTokenRef.current).catch(
          () => {},
        );
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assetId]);

  // Detect the subject + removable props when a generative outcome is chosen.
  // Billed (1 credit, idempotent per asset), so never for a matte — which has
  // no subject parameter at all. The server shapes the answer for the prompt
  // that will consume it (`?preset=ghost_mannequin` never returns the
  // mannequin as the subject, nor the garment among the removables), so the
  // prefills are re-fetched — and reset — when the shaping changes, from a
  // dialog-lifetime cache the second time around. Detection is cached
  // per-asset server-side too, so a re-fetch is a lookup, not a second VLM
  // call or a second charge.
  useEffect(() => {
    if (!isGenerative || !outcome) return;
    const serverPreset = serverPresetFor(outcome.presetId);
    const key = serverPreset === 'ghost_mannequin' ? 'ghost_mannequin' : 'raw';
    if (detectKeyRef.current === key) return;
    detectKeyRef.current = key;

    const apply = (res: { subject: string; removeSuggestions: string[] }) => {
      setDetectedRemovables(res.removeSuggestions || []);
      setRemoveTokens(res.removeSuggestions || []);
      // Never clobber a subject the user is typing right now.
      if (!editingSubjectRef.current) {
        setSubject(res.subject || '');
        setEditingSubject(false);
      }
    };

    const cached = detectCacheRef.current.get(key);
    if (cached) {
      apply(cached);
      // An abandoned in-flight fetch for the previous shaping no longer owns
      // the spinner (its late response returns without touching it).
      setDetecting(false);
      return;
    }
    setDetecting(true);
    // photoEditDetect never rejects (it catches and returns an empty result),
    // so all outcomes flow through this one handler.
    photoEditDetect(assetId, accessToken, key === 'ghost_mannequin' ? key : undefined)
      .then((res) => {
        if (cancelledRef.current) return;
        const empty = !res.subject && !res.removeSuggestions?.length;
        // A transient failure must not be remembered as "this photo has
        // nothing" for the dialog's lifetime — only real answers are cached.
        if (!empty) {
          detectCacheRef.current.set(key, res);
        }
        // The user may have switched shaping while this was in flight: a late
        // response must not overwrite the live prefill (the cache above still
        // serves it if they switch back), and the current shaping's own
        // fetch/cache path owns the spinner.
        if (detectKeyRef.current !== key) return;
        if (empty) {
          // Let re-choosing this outcome retry instead of silently giving up.
          detectKeyRef.current = null;
        }
        apply(res);
        setDetecting(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isGenerative, outcomeId]);

  const clearResult = useCallback(() => {
    if (stagedTokenRef.current) {
      photoEditDiscard(assetId, stagedTokenRef.current, accessToken).catch(() => {});
      stagedTokenRef.current = null;
    }
    setResultUrl(null);
    setMatteBytes(null);
    setPreview(null);
    setConfirmMode(null);
    setNameProblem(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assetId, accessToken]);

  const pickOutcome = (id: CutoutOutcomeId) => {
    if (id === outcomeId) return;
    clearResult();
    setOutcomeId(id);
    setAdjustOpen(false);
    setBackground('');
  };

  const changeOutcome = () => {
    clearResult();
    setOutcomeId(null);
    setAdjustOpen(false);
  };

  const runMatte = async (chosen: CutoutOutcome) => {
    const { background } = buildMatteRequest(chosen);
    const result = await getCutout(assetId, background, accessToken);
    if (cancelledRef.current) return;
    setMatteBytes({ base64: result.base64, contentType: result.contentType });
    setResultUrl(result.dataUrl);
    const charged = previewCredits(chosen);
    if (charged !== undefined) {
      showInfo(formatMessage(messages.aiEditCreditsCharged, { credits: charged }));
    }
  };

  const runGenerative = async (chosen: CutoutOutcome) => {
    const body = buildGenerativeRequest(chosen, {
      subject,
      remove: removeTokens,
      refinePrompt,
      background: background || undefined,
    });
    const data = await submitPreview(body);
    if (cancelledRef.current || !data) return;
    setPreview(data);
    stagedTokenRef.current = data.preview_token;
    setResultUrl(data.preview_url);
  };

  const handleRun = async () => {
    if (!outcome || running) return;
    clearResult();
    setRunning(true);
    try {
      if (outcome.engine === 'matte') await runMatte(outcome);
      else await runGenerative(outcome);
    } catch (e: any) {
      if (cancelledRef.current) return;
      // The matte path throws with the response status attached.
      if (e?.status === 402) {
        const body = e?.body?.data || {};
        showAlert(
          formatMessage(messages.aiEditInsufficientCredits, {
            required: body.required ?? previewCredits(outcome) ?? '?',
            balance: body.balance ?? '?',
          }),
        );
      } else {
        showAlert(e?.message || formatMessage(messages.aiEditFailed));
      }
    } finally {
      if (!cancelledRef.current) setRunning(false);
    }
  };

  const openSaveForm = (disposition: SaveDisposition) => {
    if (!outcome) return;
    setResultName(
      disposition === 'new_asset' ? `${outcome.defaultResultName}.png` : outcome.defaultResultName,
    );
    setNameProblem(null);
    setConfirmMode(disposition);
  };

  /** base64 -> Blob, so a matte result can be re-uploaded as a file. */
  const bytesToBlob = (base64: string, contentType: string) => {
    const binary = window.atob(base64);
    const bytes = Uint8Array.from(binary, (ch) => ch.charCodeAt(0));
    return new Blob([bytes], { type: contentType });
  };

  const saveMatte = async (disposition: SaveDisposition, name: string) => {
    if (!matteBytes) return;
    const blob = bytesToBlob(matteBytes.base64, matteBytes.contentType);
    // Always PNG out of the matte, whether or not it was flattened; strip any
    // extension the user kept so we never produce "name.png.png".
    const filename = `${name.split('.')[0]}.png`;
    if (disposition === 'new_asset') {
      // Left private on purpose: the upload endpoints create private assets and
      // an original is the untouched file, so nothing here publishes it. The
      // generative path gets the same treatment server-side.
      try {
        await createNewAsset(
          assetId, { file: blob, filename }, accessToken, assetData, inheritMetadata,
        );
      } catch (e: any) {
        throw new Error(formatMessage(saveErrorMessage(e?.status)));
      }
      showSuccess(formatMessage(messages.aiEditSavedNewAsset));
    } else {
      // Access comes from the upload endpoint's `/public` variant rather than a
      // follow-up publish call: that call queues a task which looks the
      // conversion up by name and skips when it is not there yet, so a
      // just-saved conversion stayed private however the user chose.
      const response = await updateAssetConversionMedia(
        assetId,
        name,
        { file: blob, filename },
        accessToken,
        isPublic,
      );
      // A refused upload (#1215) used to be cached as the new conversion and
      // reported as saved. The caller shows the thrown message.
      if (isErrorResponseResult(response)) {
        throw new Error(formatMessage(saveErrorMessage(response.status)));
      }
      queryClient.setQueryData([queryNames.asset, assetId], (data: any) => {
        if (!data) return data;
        return { ...data, conversions: { ...(data.conversions || {}), [name]: response } };
      });
      showSuccess(formatMessage(messages.aiEditSavedConversion, { conversionName: name }));
    }
  };

  const saveGenerative = async (disposition: SaveDisposition, name: string) => {
    if (!preview) return;
    const params: Record<string, any> =
      disposition === 'conversion'
        ? { conversion_name: name, public: isPublic }
        : { filename: name, inherit_metadata: inheritMetadata };
    const result = await photoEditConfirm(
      assetId,
      { preview_token: preview.preview_token, disposition, params },
      accessToken,
    );
    if (result.kind === 'error') {
      throw new Error(
        result.error?.message ||
          result.error?.error?.message ||
          formatMessage(messages.aiEditFailed),
      );
    }
    confirmedRef.current = true;
    stagedTokenRef.current = null;
    if (result.kind === 'job') {
      showInfo(
        disposition === 'conversion'
          ? formatMessage(messages.aiEditProcessingConversion, { conversionName: name })
          : formatMessage(messages.aiEditProcessingNewAsset),
      );
    } else {
      showSuccess(
        disposition === 'conversion'
          ? formatMessage(messages.aiEditSavedConversion, { conversionName: name })
          : formatMessage(messages.aiEditSavedNewAsset),
      );
    }
  };

  const handleConfirm = async () => {
    if (!outcome || !confirmMode || confirming) return;
    const problem = validateResultName(resultName, confirmMode === 'new_asset');
    setNameProblem(problem);
    if (problem) return;

    setConfirming(true);
    try {
      if (outcome.engine === 'matte') await saveMatte(confirmMode, resultName);
      else await saveGenerative(confirmMode, resultName);
      onConfirmed(confirmMode);
    } catch (e: any) {
      showAlert(e?.message || formatMessage(messages.aiEditFailed));
    } finally {
      if (!cancelledRef.current) setConfirming(false);
    }
  };

  const toggleToken = (token: string) => {
    setRemoveTokens((prev) =>
      prev.some((t) => t.toLowerCase() === token.toLowerCase())
        ? prev.filter((t) => t.toLowerCase() !== token.toLowerCase())
        : [...prev, token],
    );
  };

  // ---------------------------------------------------------------- render

  const renderTiles = () => (
    <>
      <div>
        <span className={styles.fieldLabel}>{formatMessage(messages.cutoutPrompt)}</span>
      </div>
      <div className={styles.tiles}>
        {offered.map((o) => {
          const cost = o.engine === 'matte' ? previewCredits(o) : applyCredits(o);
          return (
            <button
              key={o.id}
              type="button"
              className={styles.tile}
              onClick={() => pickOutcome(o.id)}
              data-test={`cutout-outcome-${o.id}`}
            >
              <span className={styles.tileBody}>
                <span className={styles.tileTitleRow}>
                  <span className={styles.tileTitle}>{formatMessage(messages[o.labelMsg])}</span>
                  {/* The engine is the thing a user most needs to know before
                      spending: a matte composites their own pixels, a
                      generative outcome repaints the photo. */}
                  {o.engine === 'generative' && (
                    <span className={styles.genBadge}>
                      {formatMessage(messages.cutoutGenerativeBadge)}
                    </span>
                  )}
                  {cost !== undefined && (
                    <span
                      className={
                        o.engine === 'generative' ? `${styles.cost} ${styles.costHigh}` : styles.cost
                      }
                    >
                      {formatMessage(messages.cutoutCredits, { credits: cost })}
                    </span>
                  )}
                </span>
                <span className={styles.tileDesc}>{formatMessage(messages[o.descMsg])}</span>
              </span>
            </button>
          );
        })}
      </div>
    </>
  );

  /**
   * Back to the outcome list. Above the chosen box rather than a "Change" link
   * inside it: this leaves the current selection, so it belongs outside the
   * box it leaves — the same place the modal's own Back sits.
   */
  const renderBackLink = () => (
    <button
      type="button"
      className={styles.backLink}
      onClick={changeOutcome}
      disabled={running || confirming}
      data-test="cutout-change-outcome"
    >
      <span className={styles.backCaret} aria-hidden="true">
        <CaretLeft />
      </span>
      {formatMessage(messages.cutoutBack)}
    </button>
  );

  const renderChosen = (chosen: CutoutOutcome) => {
    const cost = chosen.engine === 'matte' ? previewCredits(chosen) : applyCredits(chosen);
    return (
      <div className={styles.chosen} data-test="cutout-chosen">
        <span className={styles.chosenIcon} aria-hidden="true">
          <Checks />
        </span>
        <span className={styles.chosenBody}>
          <span className={styles.tileTitleRow}>
            <span className={styles.tileTitle}>{formatMessage(messages[chosen.labelMsg])}</span>
            {chosen.engine === 'generative' && (
              <span className={styles.genBadge}>
                {formatMessage(messages.cutoutGenerativeBadge)}
              </span>
            )}
            {cost !== undefined && (
              <span
                className={
                  chosen.engine === 'generative' ? `${styles.cost} ${styles.costHigh}` : styles.cost
                }
              >
                {formatMessage(messages.cutoutCredits, { credits: cost })}
              </span>
            )}
          </span>
          <span className={styles.tileDesc}>{formatMessage(messages[chosen.descMsg])}</span>
          {chosen.engine === 'generative' && (
            <span className={styles.genNote} data-test="cutout-generative-note">
              {formatMessage(messages.cutoutGenerativeNote)}
            </span>
          )}
        </span>
      </div>
    );
  };

  /**
   * Why a matte offers nothing to adjust. Shown inline rather than inside a
   * disclosure: a collapsible section holding one explanatory note is a click
   * that teaches nothing, and neither control below it would do anything —
   * a matte separates subject from background, so it cannot erase a prop that
   * overlaps the subject, and an instruction has no repaint to influence.
   */
  const renderMatteNote = (chosen: CutoutOutcome) => {
    const escalate = getCutoutOutcome(ESCALATE_TO)!;
    return (
      <div className={styles.note} data-test="cutout-matte-note">
        <span>
          {formatMessage(messages.cutoutAdjustBlocked, {
            outcome: formatMessage(messages[chosen.labelMsg]),
          })}
        </span>
        {/* No way out to offer if the generative addon isn't granted. */}
        {generativeAvailable && (
          <button
            type="button"
            className={styles.noteAction}
            onClick={() => pickOutcome(ESCALATE_TO)}
            data-test="cutout-escalate"
          >
            {(() => {
              const credits = applyCredits(escalate);
              return credits === undefined
                ? formatMessage(messages.cutoutSwitchToGenerativePlain, {
                    outcome: formatMessage(messages[escalate.labelMsg]),
                  })
                : formatMessage(messages.cutoutSwitchToGenerative, {
                    outcome: formatMessage(messages[escalate.labelMsg]),
                    credits,
                  });
            })()}
          </button>
        )}
      </div>
    );
  };

  /**
   * The Background row for outcomes that offer a choice (ghost mannequin:
   * transparent vs white). Rendered outside — above — the collapsed Adjust
   * section: it changes what file the user gets, so it must be visible without
   * a disclosure click, unlike the model-tuning fields below it.
   */
  const BG_LABELS: Record<string, keyof typeof messages> = {
    transparent: 'cutoutBgTransparent',
    white: 'cutoutBgWhite',
  };
  const renderBackgroundRow = (chosen: CutoutOutcome) => {
    const options = chosen.backgroundOptions || [];
    if (!options.length) return null;
    const selected = background || options[0];
    return (
      <div className={styles.field} data-test="cutout-background-row">
        <span className={styles.fieldLabel}>{formatMessage(messages.cutoutBackgroundLabel)}</span>
        <div className={styles.chipRow}>
          {options.map((option) => {
            const labelKey = BG_LABELS[option];
            const on = option === selected;
            return (
              <button
                key={option}
                type="button"
                className={on ? `${styles.chip} ${styles.chipOn}` : styles.chip}
                aria-pressed={on}
                disabled={running}
                onClick={() => {
                  if (on) return;
                  // The staged preview was generated for the other background;
                  // keeping it would let Save persist a file contradicting the
                  // selected chip. Discard it (the token is released) and let
                  // the user re-run.
                  clearResult();
                  setBackground(option);
                }}
                data-test={`cutout-background-${option}`}
              >
                {labelKey ? formatMessage(messages[labelKey]) : option}
              </button>
            );
          })}
        </div>
        <span className={styles.hint}>{formatMessage(messages.cutoutBackgroundHint)}</span>
      </div>
    );
  };

  const renderAdjust = () => (
    <div className={styles.adjust}>
      <button
        type="button"
        className={styles.adjustHead}
        onClick={() => setAdjustOpen((open) => !open)}
        aria-expanded={adjustOpen}
        data-test="cutout-adjust-toggle"
      >
        <span className={styles.caret} aria-hidden="true">
          {adjustOpen ? <CaretDown /> : <CaretRight />}
        </span>
        {formatMessage(messages.cutoutAdjust)}
        <span className={styles.adjustTail}>{formatMessage(messages.cutoutOptional)}</span>
      </button>

      {adjustOpen && (
        <div className={styles.adjustBody}>
          <div className={styles.field}>
            <span className={styles.fieldLabel}>{formatMessage(messages.cutoutSubjectLabel)}</span>
            {editingSubject ? (
              <Input
                type="text"
                variant="secondary"
                value={subject}
                disabled={running}
                onChange={setSubject}
              />
            ) : (
              <div className={styles.detected}>
                {detecting
                  ? formatMessage(messages.cutoutSubjectDetecting)
                  : subject
                    ? formatMessage(messages.cutoutSubjectDetected, { subject })
                    : formatMessage(messages.cutoutSubjectUnknown)}
                <button
                  type="button"
                  className={styles.linkButton}
                  onClick={() => setEditingSubject(true)}
                  disabled={running}
                  data-test="cutout-change-subject"
                >
                  {formatMessage(messages.cutoutChange)}
                </button>
              </div>
            )}
            <span className={styles.hint}>{formatMessage(messages.cutoutSubjectHint)}</span>
          </div>

          {detectedRemovables.length > 0 && (
            <div className={styles.field}>
              <span className={styles.fieldLabel}>{formatMessage(messages.cutoutAlsoRemove)}</span>
              <div className={styles.chipRow}>
                {detectedRemovables.map((token) => {
                  const on = removeTokens.some((t) => t.toLowerCase() === token.toLowerCase());
                  return (
                    <button
                      key={token}
                      type="button"
                      className={on ? `${styles.chip} ${styles.chipOn}` : styles.chip}
                      aria-pressed={on}
                      disabled={running}
                      onClick={() => toggleToken(token)}
                    >
                      {token}
                    </button>
                  );
                })}
              </div>
              <span className={styles.hint}>{formatMessage(messages.cutoutAlsoRemoveHint)}</span>
            </div>
          )}

          <div className={styles.field}>
            <span className={styles.fieldLabel}>
              {formatMessage(messages.cutoutRefineLabel)}{' '}
              <span className={styles.labelOptional}>
                ({formatMessage(messages.cutoutOptional)})
              </span>
            </span>
            <Textarea
              value={refinePrompt}
              rows={3}
              disabled={running}
              placeholder={formatMessage(messages.cutoutRefinePlaceholder)}
              onChange={(v: string) => setRefinePrompt(v.slice(0, REFINE_MAX))}
            />
            <ul className={styles.caveats}>
              <li>{formatMessage(messages.cutoutRefineCaveatRepaints)}</li>
              <li>{formatMessage(messages.cutoutRefineCaveatShort)}</li>
              <li>{formatMessage(messages.cutoutRefineCaveatPreview)}</li>
            </ul>
          </div>
        </div>
      )}
    </div>
  );

  const renderCost = () => {
    // Nothing chosen: the tiles each carry their own price, so a "pick one to
    // see the cost" line would only restate what is already on screen.
    if (!outcome) return null;
    if (outcome.engine === 'matte') {
      const credits = previewCredits(outcome);
      // Until the rate card resolves, say that credits are used without
      // naming a figure — a wrong number is worse than a vague one.
      return credits === undefined
        ? formatMessage(messages.aiEditUsesCredits)
        : formatMessage(messages.cutoutCostMatte, { credits });
    }
    const apply = applyCredits(outcome);
    const preview = previewCredits(outcome);
    const detect = detectCredits(outcome);
    if (apply === undefined || preview === undefined || detect === undefined) {
      return formatMessage(messages.aiEditUsesCredits);
    }
    return formatMessage(messages.cutoutCostGenerative, { apply, preview, detect });
  };

  const renderActionBar = () => {
    if (confirmMode) {
      return (
        <SaveResultForm
          disposition={confirmMode}
          value={resultName}
          onChange={setResultName}
          onBack={() => setConfirmMode(null)}
          onConfirm={handleConfirm}
          problem={nameProblem}
          confirming={confirming}
          isPublic={isPublic}
          onAccessChange={setIsPublic}
          inheritMetadata={inheritMetadata}
          onInheritMetadataChange={setInheritMetadata}
        />
      );
    }

    if (resultUrl) {
      return (
        <>
          <span className={styles.actionContext}>{formatMessage(messages.aiEditSaveResult)}</span>
          <div className={styles.actionButtons}>
            <Button mode="flat" size="small" onClick={handleRun} loading={running}>
              {formatMessage(messages.aiEditReapply)}
            </Button>
            <Button mode="flat" size="small" onClick={clearResult}>
              {formatMessage(messages.aiEditStartOver)}
            </Button>
            <Button mode="flat" size="small" onClick={() => openSaveForm('conversion')}>
              {formatMessage(messages.aiEditAddConversion)}
            </Button>
            {/* New asset is the default: a cut-out is usually a deliverable in
                its own right, not another format of the source. */}
            <Button mode="primary" size="small" onClick={() => openSaveForm('new_asset')}>
              {formatMessage(messages.aiEditAddNewAsset)}
            </Button>
          </div>
        </>
      );
    }

    const cost = renderCost();
    return (
      <>
        {cost && <span className={styles.actionContext}>{cost}</span>}
        {/* Grouped so Apply stays right-aligned when there is no cost line to
            push it there (the bar is space-between). */}
        <div className={styles.actionButtons}>
          <Button
            mode="primary"
            size="small"
            onClick={handleRun}
            loading={running}
            disabled={!outcome || running}
            data-test="cutout-preview"
          >
            {formatMessage(messages.aiEditApply)}
          </Button>
        </div>
      </>
    );
  };

  return (
    <div className={styles.panel}>
      <div className={styles.body}>
        <CompareSlider
          originalUrl={originalUrl}
          resultUrl={resultUrl}
          alt={assetName}
          busy={running}
          busyLabel={formatMessage(messages.aiEditApplying)}
          beforeLabel={formatMessage(messages.aiEditBefore)}
          afterLabel={formatMessage(messages.aiEditAfter)}
        />
        <div className={styles.controls}>
          {outcome ? (
            <>
              {renderBackLink()}
              {renderChosen(outcome)}
              {renderBackgroundRow(outcome)}
              {outcome.allowsAdjust ? renderAdjust() : renderMatteNote(outcome)}
            </>
          ) : (
            renderTiles()
          )}
        </div>
      </div>
      <div className={styles.actionBar}>{renderActionBar()}</div>
    </div>
  );
};

export default CutoutDialog;
